import imageCompression from "browser-image-compression";

export type CompressPreset = "avatar" | "noticeboard" | "activity";

const PRESETS: Record<CompressPreset, { maxWidthOrHeight: number; maxSizeMB: number }> = {
  avatar: { maxWidthOrHeight: 512, maxSizeMB: 0.2 },
  noticeboard: { maxWidthOrHeight: 1600, maxSizeMB: 0.4 },
  activity: { maxWidthOrHeight: 1920, maxSizeMB: 1 },
};

const SKIP_TYPES = new Set(["image/gif", "image/svg+xml"]);

/**
 * Compress an image in the browser before upload. Always falls back to the
 * original file on any error so uploads never break.
 */
export async function compressImage(file: File, preset: CompressPreset): Promise<File> {
  try {
    if (!file.type.startsWith("image/")) return file;
    if (SKIP_TYPES.has(file.type)) return file;
    // Small files: skip the work.
    if (file.size < 150 * 1024) return file;

    // WebP keeps transparency and is far smaller than PNG, so PNGs no longer
    // get shrunk to a tiny resolution just to hit the size target.
    const keepTransparent = false;
    const { maxWidthOrHeight, maxSizeMB } = PRESETS[preset];

    const compressed = await imageCompression(file, {
      maxWidthOrHeight,
      maxSizeMB,
      useWebWorker: true,
      initialQuality: 0.8,
      fileType: keepTransparent ? "image/png" : "image/webp",
    });

    if (compressed.size >= file.size) return file;

    const targetExt = keepTransparent ? "png" : "webp";
    const targetType = keepTransparent ? "image/png" : "image/webp";
    const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([compressed], `${baseName}.${targetExt}`, {
      type: targetType,
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn("[imageCompression] fallback to original", err);
    return file;
  }
}
